import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import UserImg from "@styles/all-images/images/Avatar.png";
import ArrowDownIcon from "@styles/all-images/icons/chevron-down-grey.svg";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import AddPeopleGroupLiveChatSkeleton from "../components/skeletons/AddPeopleGroupLiveChatSkeleton";
import AutoCompleteSearch, {
  AutoCompleteSearchRef,
} from "@/components/common/inputs/AutoCompleteSearch";
import { Skeleton } from "@mui/material";
import { S3Image } from "@/utils/s3-helper";
import UserDropdown from "@styles/all-images/icons/user-dropdown.svg";

interface TierUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  user_type: {
    id: number;
    user_type: string;
  };
  profile_picture: string | null;
  permission_profiles: Array<{
    id: number;
    name: string;
    roles: Array<{
      id: number;
      name: string;
    }>;
  }>;
}

interface Company {
  id: number;
  name: string;
  users: TierUser[];
}

interface TierData {
  id: number;
  user_type: string;
  companies: Company[];
}

const AddPeopleGroupLiveChat = () => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [expandedTier, setExpandedTier] = useState<string | null>(null);
  const [expandedCompany, setExpandedCompany] = useState<string | null>(null);
  const [tiers, setTiers] = useState<TierData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [existingMembers, setExistingMembers] = useState<number[]>([]);
  const [groupNameLoading, setGroupNameLoading] = useState(true);
  const [groupName, setGroupName] = useState("");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const isList = searchParams.get("list") === "true";
  const autoCompleteSearchRef = useRef<AutoCompleteSearchRef>(null);

  // Separate callback for fetching existing members
  const fetchExistingMembers = useCallback(async () => {
    if (!id) return;
    try {
      const { authenticatedCall } = actionsHandler("getGroupChatMembersById");
      const response = await dispatch(
        authenticatedCall({
          method: "get",
          customParams: [id + "/"],
        }),
      );

      if (response.ok) {
        const memberIds = response.data.data.map((member: any) => member.id);
        setExistingMembers(memberIds);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  }, [dispatch, id]);

  // Combined callback for fetching tier data
  const fetchTierData = useCallback(
    async (userId?: number) => {
      try {
        autoCompleteSearchRef.current?.handleClose();
        setIsLoading(true);
        const { authenticatedCall } = actionsHandler("getTierCompanyUsers");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customQuery: userId ? `user_id=${userId}` : undefined,
          }),
        );

        if (response.ok) {
          setTiers(response.data);
          if (response.data.length > 0) {
            const tier = response.data[0];
            setExpandedTier(`T${tier.id}`);
            if (tier.companies.length > 0) {
              setExpandedCompany(`T${tier.id}C${tier.companies[0].id}`);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching tier data:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch],
  );

  // Initial data fetch
  useEffect(() => {
    fetchTierData();
    fetchExistingMembers();
  }, [fetchTierData, fetchExistingMembers]);

  // Add group name fetching
  const fetchGroupDetails = useCallback(async () => {
    if (!id) return;
    try {
      setGroupNameLoading(true);
      const { authenticatedCall } = actionsHandler("createGroupChatById");
      const response = await dispatch(
        authenticatedCall({
          method: "get",
          customParams: [`${id}/`],
        }),
      );

      if (response.ok) {
        setGroupName(response.data.name);
      }
    } catch (error) {
      console.error("Error fetching group details:", error);
    } finally {
      setGroupNameLoading(false);
    }
  }, [dispatch, id]);

  useEffect(() => {
    fetchGroupDetails();
  }, [fetchGroupDetails]);

  const handleTierClick = (tierId: number) => {
    setExpandedTier(expandedTier === `T${tierId}` ? null : `T${tierId}`);
    setExpandedCompany(null);
  };

  const handleCompanyClick = (tierId: number, companyId: number) => {
    setExpandedCompany(
      expandedCompany === `T${tierId}C${companyId}`
        ? null
        : `T${tierId}C${companyId}`,
    );
  };

  // User select handler
  const handleUserSelect = useCallback(
    async (user: any) => {
      if (selectedUsers.includes(user.id)) {
        setSelectedUsers(selectedUsers.filter((id) => id !== user.id));
      } else {
        setSelectedUsers([...selectedUsers, user.id]);
      }
    },
    [selectedUsers],
  );

  const handleSearchClick = (user: any) => {
    fetchTierData(user?.id);
  };

  // Search handler
  const handleSearch = useCallback(
    async (query: string) => {
      if (!query || query.length < 1) return [];

      try {
        setSearchLoading(true);
        const { authenticatedCall } = actionsHandler("searchUsers");
        const response = await dispatch(
          authenticatedCall({
            customQuery: `search=${query}`,
            method: "get",
          }),
        );

        return response.ok ? response.data.data : [];
      } catch (error) {
        console.error("Error searching users:", error);
        return [];
      } finally {
        setSearchLoading(false);
      }
    },
    [dispatch],
  );

  const handleSubmit = async () => {
    if (selectedUsers.length === 0) {
      toast.error("Please select at least one user");
      return;
    }

    if (!id) {
      toast.error("Group ID not found");
      return;
    }

    try {
      const { authenticatedCall } = actionsHandler("addGroupMembers");
      const response = await dispatch(
        authenticatedCall({
          method: "post",
          payload: { member_ids: selectedUsers },
          formattingParams: { id },
        }),
      );

      if (response.ok) {
        toast.success(response.data.message);
        navigate(
          isList
            ? `/management-teams-group-members/${id}`
            : `/live-chat?screen=group&id=${id}`,
        );
      }
    } catch (error) {
      console.error("Error adding members:", error);
    }
  };

  return (
    <div className="tw-relative">
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <a
                href={
                  isList
                    ? `/management-teams-group-members/${id}`
                    : `/live-chat?screen=group&id=${id}`
                }
              >
                <img src={BackIcon} alt="Back Icon" />
              </a>
              {groupNameLoading ? (
                <Skeleton width={500} height={32} className="tw-mx-2" />
              ) : (
                <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323] tw-flex tw-items-center">
                  Add people to <span className="tw-mx-2">'{groupName}'</span>
                  Group
                </h1>
              )}
            </div>
            <div>
              <button
                onClick={handleSubmit}
                className="tw-bg-[#146C94] tw-py-3 tw-px-4 tw-text-white tw-text-sm tw-font-semibold tw-rounded-lg"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="tw-p-6 tw-mt-[147px] tw-gap-6 tw-flex-col tw-flex">
        <div>
          <div className="tw-relative tw-w-[30%]">
            <AutoCompleteSearch
              ref={autoCompleteSearchRef}
              onSearch={handleSearch}
              onSelect={handleSearchClick}
              isLoading={searchLoading}
              placeholder="Search users..."
              noResultsMessage="No users found"
            />
          </div>
        </div>
        <div>
          <div>
            <div className="tw-full">
              {isLoading ? (
                <AddPeopleGroupLiveChatSkeleton />
              ) : (
                tiers.map((tier) => (
                  <div
                    key={tier.id}
                    className="tw-rounded-lg tw-overflow-hidden tw-bg-[#FFFFFF] tw-border tw-border-[#E9EAEB] tw-shadow-sm tw-mb-3"
                  >
                    <button
                      onClick={() => handleTierClick(tier.id)}
                      className={`tw-w-full tw-flex tw-items-center tw-justify-between tw-p-4 tw-text-left tw-transition-colors 
                        ${expandedTier === `T${tier.id}` ? "tw-bg-[#ECF1F8] tw-text-[#232323]" : ""}`}
                    >
                      <span className="tw-font-medium">{tier.user_type}</span>
                      <img
                        src={ArrowDownIcon}
                        alt="Arrow Down Icon"
                        className={`tw-transition-transform ${expandedTier === `T${tier.id}` ? "tw-rotate-180" : ""}`}
                      />
                    </button>

                    {expandedTier === `T${tier.id}` && (
                      <div className="tw-p-5 tw-bg-white tw-border-t">
                        {tier.companies.map((company) => (
                          <div key={company.id}>
                            <div className="tw-rounded-lg tw-overflow-hidden tw-bg-[#FFFFFF] tw-border tw-border-[#E9EAEB] tw-shadow-sm tw-mb-3">
                              <button
                                onClick={() =>
                                  handleCompanyClick(tier.id, company.id)
                                }
                                className={`tw-w-full tw-flex tw-items-center tw-justify-between tw-p-4 tw-text-left tw-transition-colors 
                                  ${expandedCompany === `T${tier.id}C${company.id}` ? "tw-bg-[#ECF1F8] tw-text-[#232323]" : ""}`}
                              >
                                <span className="tw-font-medium">
                                  {company.name}
                                </span>
                                <img
                                  src={ArrowDownIcon}
                                  alt="Arrow Down Icon"
                                  className={`tw-transition-transform ${expandedCompany === `T${tier.id}C${company.id}` ? "tw-rotate-180" : ""}`}
                                />
                              </button>

                              {expandedCompany ===
                                `T${tier.id}C${company.id}` && (
                                <div className="tw-p-4 tw-bg-white tw-border-t">
                                  <div className="tw-grid tw-grid-cols-4 tw-md:tw-grid-cols-2 tw-lg:tw-grid-cols-4 tw-gap-4">
                                    {company.users.map((user) => (
                                      <div
                                        key={user.id}
                                        className={`tw-px-4 tw-py-3 tw-bg-white tw-rounded-lg tw-border tw-border-[#E9EAEB] tw-flex tw-items-start tw-justify-between tw-space-x-3 ${
                                          existingMembers.includes(user.id)
                                            ? "tw-opacity-50"
                                            : ""
                                        }`}
                                      >
                                        <div className="tw-flex tw-items-center tw-gap-4">
                                          <div className="tw-w-[45px] tw-h-[45px] tw-bg-[#ECF1F8] tw-rounded-full tw-overflow-hidden">
                                            {user.profile_picture ? (
                                              <S3Image
                                                src={
                                                  user.profile_picture?.replace(
                                                    "https%3A/9spl-sentinel.s3.us-east-1.amazonaws.com/",
                                                    "",
                                                  ) || null
                                                }
                                                alt="Profile"
                                                className="tw-w-full tw-h-full tw-rounded-full"
                                                cacheKey={user.profile_picture}
                                              />
                                            ) : (
                                              <img
                                                src={UserDropdown}
                                                alt="Profile"
                                                className="tw-w-full tw-h-full tw-rounded-full"
                                              />
                                            )}
                                          </div>
                                          <div>
                                            <p className="tw-text-[#232323] tw-font-semibold tw-text-sm tw-mb-[2px]">
                                              {`${user.first_name} ${user.last_name}`}
                                            </p>
                                            <p className="tw-text-[#475569] tw-text-xs tw-font-normal">
                                              {user.email}
                                            </p>
                                          </div>
                                        </div>
                                        <input
                                          type="checkbox"
                                          checked={selectedUsers.includes(
                                            user.id,
                                          )}
                                          onChange={() =>
                                            handleUserSelect(user)
                                          }
                                          disabled={existingMembers.includes(
                                            user.id,
                                          )}
                                          className="tw-ml-auto tw-w-4 tw-h-4 tw-text-blue-600 tw-rounded tw-border-gray-300 tw-focus:tw-ring-blue-500 disabled:tw-opacity-50"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPeopleGroupLiveChat;
